import type { LayerDescriptor, Vec3, Vec4 } from "eutopia.ts";
import {
  container,
  createLayer,
  createLayerType,
  line,
  polygon,
} from "eutopia.ts";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  GeometryCollection,
} from "geojson";
import { $, resolve } from "signlets";

import { parseColor, scaleOpacity, transparent, white } from "./color";
import { marker } from "./marker";

export type OverlayProps = {
  overlay: FeatureCollection<Geometry> | undefined;
};

type SimpleFeature = Omit<Feature<Geometry, GeoJsonProperties>, "geometry"> & {
  geometry: Exclude<Geometry, GeometryCollection>;
};

const toVec3 = ([lon = 0, lat = 0, altitude = 0]: number[]): Vec3 => [
  lon,
  lat,
  altitude,
];

export const overlay = createLayerType<OverlayProps>(
  (context, { overlay: overlayProp }) =>
    createLayer(
      context,
      container({
        layers: $(() => {
          const _overlay = resolve(overlayProp);
          if (!_overlay) return [];

          const features: SimpleFeature[] = _overlay.features.flatMap(
            (feature): SimpleFeature[] =>
              feature.geometry.type === "GeometryCollection"
                ? feature.geometry.geometries.map(geometry => ({
                    ...feature,
                    geometry: geometry as Exclude<Geometry, GeometryCollection>,
                  }))
                : [
                    feature as Feature<
                      Exclude<Geometry, GeometryCollection>,
                      GeoJsonProperties
                    >,
                  ],
          );

          const layers: LayerDescriptor[] = features.flatMap(
            (feature): LayerDescriptor[] => {
              const { geometry, properties } = feature;

              const strokeOpacity =
                properties?.["stroke-opacity"] !== undefined
                  ? parseFloat(properties["stroke-opacity"] as string)
                  : 1;
              const stroke = scaleOpacity(
                properties?.stroke
                  ? parseColor(properties.stroke as string)
                  : white,
                strokeOpacity,
              ) as Vec4;
              const strokeWidth =
                properties?.["stroke-width"] !== undefined
                  ? parseFloat(properties["stroke-width"] as string)
                  : 2;

              switch (geometry.type) {
                case "Point": {
                  const color: Vec4 = properties?.["marker-color"]
                    ? parseColor(properties["marker-color"] as string)
                    : transparent;
                  return [
                    marker({
                      markers: [
                        {
                          position: toVec3(geometry.coordinates),
                          color,
                        },
                      ],
                    }),
                  ];
                }
                case "LineString":
                case "MultiLineString": {
                  const rings =
                    geometry.type === "LineString"
                      ? [geometry.coordinates.map(toVec3)]
                      : geometry.coordinates.map(r => r.map(toVec3));
                  return [
                    line({
                      vertices: rings.map(points =>
                        points.map(position => ({
                          position,
                          color: stroke,
                          width: 1,
                          minWidthPixels: strokeWidth,
                        })),
                      ),
                      depth: false,
                    }),
                  ];
                }
                case "Polygon": {
                  const fillOpacity =
                    properties?.["fill-opacity"] !== undefined
                      ? parseFloat(properties["fill-opacity"] as string)
                      : 1;
                  const fillColor = scaleOpacity(
                    properties?.fill
                      ? parseColor(properties.fill as string)
                      : transparent,
                    fillOpacity,
                  ) as Vec4;
                  const rings = geometry.coordinates.map(r => r.map(toVec3));
                  return [
                    polygon({
                      rings: rings.map(_ => _.slice(1)),
                      color: fillColor,
                      depth: false,
                    }),
                    line({
                      vertices: rings.map(points =>
                        points.map(position => ({
                          position,
                          color: stroke,
                          width: 1,
                          minWidthPixels: strokeWidth,
                        })),
                      ),
                      depth: false,
                    }),
                  ];
                }
              }

              return [];
            },
          );

          return layers;
        }),
      }),
    ),
);
